/**
 * Mobile sticky add-to-cart bar. Shows once the real add-to-cart form scrolls
 * out of view (via IntersectionObserver -- correct regardless of PDP content
 * height, unlike a fixed scroll-offset), submits the SAME real Dawn form via
 * the native HTML `form="<id>"` button attribute (found at runtime, not
 * assumed -- see sections/sticky-atc.liquid for why), and stays in sync with
 * variant selection via PUB_SUB_EVENTS.variantChange, whose payload shape
 * (`{ data: { sectionId, html, variant } }`) was confirmed by reading
 * assets/product-info.js directly rather than assumed.
 *
 * Wrapped in an IIFE + customElements.get() guard for the same reason as
 * free-shipping-bar.js: this <script> tag is only rendered once per page here
 * (sticky-atc is its own section, not duplicated across template fragments
 * the way the cart snippets are), but the guard costs nothing and matches the
 * pattern established after that bug.
 */
(function () {
  if (customElements.get('sticky-atc')) return;

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.realForm = document.querySelector('form[data-type="add-to-cart-form"]');
      this.button = this.querySelector('[data-sticky-atc-button]');
      this.priceEl = this.querySelector('.sticky-atc__price');

      if (!this.realForm || !this.button) return; // nothing sane to wire up

      // getAttribute('id'), NOT the `.id` DOM property. Confirmed live this
      // matters: Dawn's buy-buttons.liquid puts a hidden `<input name="id">`
      // (the variant id field) inside the form, and this browser's named-item
      // shadowing resolves `form.id` to THAT INPUT ELEMENT instead of the
      // form's id attribute string -- `setAttribute('form', formEl.id)` was
      // stringifying to the literal text "[object HTMLInputElement]", which
      // silently failed to associate the button with the form at all.
      const realFormId = this.realForm.getAttribute('id');
      if (!realFormId) {
        // Dawn's buy-buttons.liquid always sets `id: product_form_id` on the
        // real form, but if a customized theme ever omits it, fail safe by
        // staying hidden rather than submitting to a form that doesn't exist.
        return;
      }

      this.button.setAttribute('form', realFormId);
      this.button.removeAttribute('disabled');

      this.observeRealButton();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
      }
    }

    observeRealButton() {
      const observer = new IntersectionObserver(
        (entries) => {
          const isRealButtonVisible = entries[0].isIntersecting;
          this.toggleAttribute('hidden', isRealButtonVisible);
        },
        { rootMargin: '0px 0px -10% 0px' },
      );
      observer.observe(this.realForm);
    }

    onVariantChange(event) {
      const variant = event?.data?.variant;
      if (!variant) {
        // No variant matches the current option combination (e.g. an
        // out-of-stock combo with no fallback) -- don't offer to add it.
        this.button.setAttribute('disabled', 'disabled');
        return;
      }
      if (this.priceEl && typeof variant.price === 'number') {
        // variant.price is already in the shop's smallest currency unit
        // (cents for USD), same convention as cart.js's total_price.
        this.priceEl.textContent = (variant.price / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        });
      }
      if (variant.available) {
        this.button.removeAttribute('disabled');
      } else {
        this.button.setAttribute('disabled', 'disabled');
      }
    }
  }

  customElements.define('sticky-atc', StickyAtc);
})();
